import type { SettingsResponse } from "@go-novus/admin-api";
import { differenceInMilliseconds, isValid, parseISO } from "date-fns";

export type SupportEmailVerification =
  SettingsResponse["handoff"]["supportEmailVerification"];

/**
 * 最近一次点「发送 / 重发」的结果，只在本次打开页面时有效。
 * 记下是给哪个邮箱发的：保存了新邮箱后，旧邮箱的结果不能盖住新邮箱的真实状态。
 */
export interface SupportEmailSendResult {
  email: string;
  result: "sent" | "failed" | "unavailable";
}

/** ISO 时刻转毫秒；缺值或格式不对时为 null。 */
function readInstant(iso: string | null): number | null {
  if (!iso) return null;
  const instant = parseISO(iso);
  return isValid(instant) ? instant.getTime() : null;
}

/**
 * 支持邮箱输入框下方显示什么：
 * 状态标记跟着已保存的邮箱走，提示框只在已保存、未验证时出现。
 */
export type SupportEmailVerificationView =
  | { kind: "empty" }
  | { kind: "unsaved" }
  | { kind: "verified" }
  | { kind: "unsent" }
  | { kind: "pending"; lastSentAt: number | null; resent: boolean }
  | { kind: "failed" | "unavailable" };

export function readSupportEmailVerificationView(input: {
  draftEmail: string;
  savedEmail: string;
  verification: SupportEmailVerification;
  lastResult: SupportEmailSendResult | undefined;
}): SupportEmailVerificationView {
  const { draftEmail, savedEmail, verification } = input;
  const lastResult =
    input.lastResult?.email === savedEmail
      ? input.lastResult.result
      : undefined;
  if (draftEmail.trim() !== savedEmail)
    return draftEmail.trim() ? { kind: "unsaved" } : { kind: "empty" };
  if (!savedEmail) return { kind: "empty" };
  if (verification.status === "verified") return { kind: "verified" };
  if (lastResult === "failed" || lastResult === "unavailable")
    return { kind: lastResult };
  if (verification.status === "unsent") return { kind: "unsent" };
  return {
    kind: "pending",
    lastSentAt: readInstant(verification.lastSentAt),
    resent: lastResult === "sent",
  };
}

/** 还要等几秒才能重发；不用等时为 0。 */
export function readResendWaitSeconds(
  resendAvailableAt: string | null,
  now: number,
): number {
  const availableAt = readInstant(resendAvailableAt);
  if (availableAt === null) return 0;
  const wait = differenceInMilliseconds(availableAt, now);
  return wait > 0 ? Math.ceil(wait / 1000) : 0;
}
